<script setup lang="ts">
import lightGallery from 'lightgallery'
import type { GalleryItem } from 'lightgallery/lg-utils'
import lgThumbnail from 'lightgallery/plugins/thumbnail'
import lgZoom from 'lightgallery/plugins/zoom'
import lgFullscreen from 'lightgallery/plugins/fullscreen'
import lgAutoplay from 'lightgallery/plugins/autoplay'
import 'lightgallery/css/lightgallery.css'
import 'lightgallery/css/lg-thumbnail.css'
import 'lightgallery/css/lg-zoom.css'
import 'lightgallery/css/lg-fullscreen.css'
import 'lightgallery/css/lg-autoplay.css'
import type { LightboxImage } from '~/composables/useImageLightbox'
import { toStaticGalleryItem } from '~/utils/lightbox.mjs'

defineOptions({ inheritAttrs: false })

const props = defineProps<{
  visible: boolean
  sources: LightboxImage[]
  index?: number
}>()

const emit = defineEmits<{
  (event: 'hide'): void
}>()

const rootRef = ref<HTMLElement | null>(null)
let gallery: ReturnType<typeof lightGallery> | null = null
const lightGalleryLicenseKey = 'yuelaiengine'

const galleryItems = computed(() =>
  props.sources
    .map(source => toStaticGalleryItem(source) as GalleryItem | null)
    .filter((item): item is GalleryItem => item !== null)
)

const clampIndex = (total: number) =>
  Math.min(Math.max(Number.isFinite(props.index) ? Number(props.index) : 0, 0), total - 1)

const handleAfterClose: EventListener = () => {
  if (!props.visible) {
    return
  }
  emit('hide')
}

const ensureGallery = (items: GalleryItem[]) => {
  if (!rootRef.value) {
    return null
  }

  if (!gallery) {
    gallery = lightGallery(rootRef.value, {
      licenseKey: lightGalleryLicenseKey,
      dynamic: true,
      dynamicEl: items,
      plugins: [lgThumbnail, lgZoom, lgFullscreen, lgAutoplay],
      download: false,
      counter: true,
      hideScrollbar: true,
      zoomFromOrigin: false,
      thumbnail: true,
      toggleThumb: true,
      animateThumb: true,
      allowMediaOverlap: true,
      actualSize: true,
      actualSizeIcons: { zoomIn: 'lg-actual-size', zoomOut: 'lg-zoom-out' },
      showZoomInOutIcons: true,
      getCaptionFromTitleOrAlt: false,
      mobileSettings: {
        controls: false,
        showZoomInOutIcons: false,
        fullScreen: false,
      }
    })

    rootRef.value.addEventListener('lgAfterClose', handleAfterClose)
  }

  return gallery
}

const syncGallery = async () => {
  const items = galleryItems.value

  if (!props.visible) {
    if (gallery?.lgOpened) {
      gallery.closeGallery(true)
    }
    return
  }

  if (!items.length) {
    emit('hide')
    return
  }

  await nextTick()

  const instance = ensureGallery(items)
  if (!instance) {
    return
  }

  const targetIndex = clampIndex(items.length)
  if (instance.lgOpened) {
    instance.updateSlides(items, targetIndex)
    instance.slide(targetIndex)
  } else {
    instance.refresh(items)
    instance.openGallery(targetIndex)
  }
}

watch(
  [() => props.visible, () => props.index, () => props.sources],
  () => {
    void syncGallery()
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  if (rootRef.value) {
    rootRef.value.removeEventListener('lgAfterClose', handleAfterClose)
  }
  if (gallery) {
    gallery.destroy()
    gallery = null
  }
})
</script>

<template>
  <div ref="rootRef" class="blog-lightgallery-root" />
</template>

<style scoped>
.blog-lightgallery-root {
  width: 0;
  height: 0;
  overflow: hidden;
}
</style>

<style>
.lg-container .lg-image {
  dynamic-range-limit: no-limit;
}

.blog-lightbox-caption,
.blog-lightbox-caption > div {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.blog-lightbox-caption p {
  margin: 0;
}

.blog-lightbox-badge {
  display: inline-flex;
  padding: 2px 5px;
  color: #fff;
  font-size: 9px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.04em;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 4px;
}
</style>
